import React, { useState, useEffect } from "react";
import {
    Box,
    VStack,
    HStack,
    Text,
    Button,
    useToast,
    useColorModeValue,
    Select,
    Badge,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Icon,
    Divider,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    useDisclosure,
} from "@chakra-ui/react";
import {
    collection,
    query,
    getDocs,
    addDoc,
    updateDoc,
    doc,
    serverTimestamp,
    orderBy,
    where,
    limit,
    Timestamp
} from "firebase/firestore";
import { db } from "../../firebase/config";
import PageShell from "../layout/PageShell";
import { FiPlus, FiMoreVertical, FiCheckCircle, FiClock, FiLayers, FiTrash2, FiSave, FiEdit3 } from "react-icons/fi";
import FieldMappingTool from "./common/FieldMappingTool";
import { ONLINE_STATIC_ORDER, LOCAL_STATIC_ORDER } from "./common/static_samples";

interface MappingDoc {
    id: string;
    name: string;
    apiName: string;
    entityType: string;
    version: number;
    active: boolean;
    mapping: {
        [key: string]: {
            sources: { field: string }[];
            pipeline: { id: string; type: string; params: any }[];
        }
    };
    createdAt: Timestamp;
    createdBy: string;
}

const API_SCHEMAS: { [key: string]: { entity: string, targets: string[] } } = {
    SuperRoute: {
        entity: "Order",
        targets: ["name", "telephone", "email", "postcode", "street_number", "street_name", "apt", "buzz_code", "city", "province", "country", "ref", "order_code", "note"]
    },
    ShipDay: {
        entity: "Order",
        targets: ["orderNumber", "customerName", "customerAddress", "customerEmail", "customerPhoneNumber", "restaurantName", "deliveryInstruction"]
    },
    LivroNuage: {
        entity: "Order",
        targets: [
            "pickup_name", "pickup_address", "pickup_phone_number", "pickup_business_name",
            "pickup_latitude", "pickup_longitude", "pickup_notes", "pickup_instructions",
            "dropoff_name", "dropoff_address", "dropoff_phone_number", "dropoff_business_name",
            "dropoff_latitude", "dropoff_longitude", "dropoff_notes", "dropoff_instructions",
            "dropoff_seller_notes", "manifest_items", "manifest_total_value", "manifest_reference",
            "external_id", "quote_id", "deliverable_action", "undeliverable_action", "tip",
            "idempotency_key", "external_store_id"
        ]
    }
};

const SAMPLE_ORDER_DEFAULT = {
    orderId: "ORD-12345",
    customerName: "John Doe",
    customerPhone: "555-0199",
    customerEmail: "john@example.com",
    deliveryAddress: {
        line1: "123 Main St",
        city: "Vancouver",
        province: "BC",
        postalCode: "V6B 2W9",
        country: "Canada"
    },
    items: [
        { name: "Burger", price: 15.00, quantity: 1 }
    ],
    total: 15.00,
    note: "Leave at front door"
};

const APIMappingTool: React.FC = () => {
    const [mappings, setMappings] = useState<MappingDoc[]>([]);
    const [selectedApi, setSelectedApi] = useState("SuperRoute");
    const [selectedMapping, setSelectedMapping] = useState<MappingDoc | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [sampleData, setSampleData] = useState<any>(SAMPLE_ORDER_DEFAULT);
    const [sampleSource, setSampleSource] = useState<"mock" | "online_static" | "online_live" | "local_static" | "local_live">("mock");
    const [newMappingName, setNewMappingName] = useState("");

    // For editing name
    const [mappingToRename, setMappingToRename] = useState<MappingDoc | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const { isOpen: isRenameOpen, onOpen: onRenameOpen, onClose: onRenameClose } = useDisclosure();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const bgCard = useColorModeValue("white", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const textColor = useColorModeValue("gray.700", "white");
    const bgApp = useColorModeValue("gray.50", "gray.900");

    useEffect(() => {
        loadMappings();
    }, []);

    const loadMappings = async () => {
        setIsLoading(true);
        try {
            const q = query(
                collection(db, "apiMappings"),
                orderBy("createdAt", "desc")
            );
            const snapshot = await getDocs(q);
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MappingDoc));
            setMappings(data);

            // Auto-select active one for current API
            const active = data.find(m => m.apiName === selectedApi && m.active);
            if (active) setSelectedMapping(active);
        } catch (error) {
            console.error("Error loading mappings:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const createNewVersion = async () => {
        if (!newMappingName.trim()) {
            toast({ title: "Please enter a name", status: "warning" });
            return;
        }

        const lastVersion = mappings.filter(m => m.apiName === selectedApi).length;
        const newMapping = {
            name: newMappingName,
            apiName: selectedApi,
            entityType: API_SCHEMAS[selectedApi].entity,
            version: lastVersion + 1,
            active: false,
            mapping: selectedMapping?.mapping || {},
            createdAt: serverTimestamp(),
            createdBy: "SuperAdmin"
        };

        try {
            const docRef = await addDoc(collection(db, "apiMappings"), newMapping);
            const created = { id: docRef.id, ...newMapping } as any as MappingDoc;
            setMappings([created, ...mappings]);
            setSelectedMapping(created);
            setIsEditing(true);
            setNewMappingName("");
            onClose();
            toast({ title: "New mapping created", status: "success" });
        } catch (error) {
            toast({ title: "Error creating mapping", status: "error" });
        }
    };

    const handleSave = async () => {
        if (!selectedMapping) return;
        try {
            await updateDoc(doc(db, "apiMappings", selectedMapping.id), {
                mapping: selectedMapping.mapping,
                updatedAt: serverTimestamp()
            });
            setIsEditing(false);
            toast({ title: "Mapping saved", status: "success" });
        } catch (error) {
            toast({ title: "Error saving", status: "error" });
        }
    };

    const handleRename = async () => {
        if (!mappingToRename || !renameValue.trim()) return;
        try {
            await updateDoc(doc(db, "apiMappings", mappingToRename.id), {
                name: renameValue,
                updatedAt: serverTimestamp()
            });
            setMappings(mappings.map(m => m.id === mappingToRename.id ? { ...m, name: renameValue } : m));
            if (selectedMapping?.id === mappingToRename.id) {
                setSelectedMapping({ ...selectedMapping, name: renameValue });
            }
            onRenameClose();
            toast({ title: "Mapping renamed", status: "success" });
        } catch (error) {
            toast({ title: "Error renaming", status: "error" });
        }
    };

    const fetchSampleData = async (type: "online" | "local") => {
        setIsLoading(true);
        try {
            const possibleTimestamps = ["created", "createdAt", "paidAt", "lastUpdated"];
            let fetchedDoc = null;

            // Try each timestamp for ordering
            for (const tsField of possibleTimestamps) {
                let q;
                if (type === "online") {
                    q = query(
                        collection(db, "orders"),
                        where("channel", "in", ["Online", "ONLINE", "online"]),
                        orderBy(tsField, "desc"),
                        limit(1)
                    );
                } else {
                    q = query(
                        collection(db, "orders"),
                        where("orderChannel", "in", ["Delivery", "DELIVERY", "delivery"]),
                        where("deliveryType", "in", ["Local", "LOCAL", "local"]),
                        orderBy(tsField, "desc"),
                        limit(1)
                    );
                }

                try {
                    const snap = await getDocs(q);
                    if (!snap.empty) {
                        fetchedDoc = snap.docs[0].data();
                        break;
                    }
                } catch (e) {
                    console.warn(`Query failed with timestamp field: ${tsField}`, e);
                    continue; // Try next timestamp
                }
            }

            // Fallback for Online (if channel field is not being used)
            if (!fetchedDoc && type === "online") {
                const q2 = query(
                    collection(db, "orders"),
                    where("orderChannel", "in", ["Online", "ONLINE", "online"]),
                    orderBy("created", "desc"),
                    limit(1)
                );
                const snap2 = await getDocs(q2);
                if (!snap2.empty) fetchedDoc = snap2.docs[0].data();
            }

            // Final fallback: No ordering, just get 1st matching doc (very resilient)
            if (!fetchedDoc) {
                let q3;
                if (type === "online") {
                    q3 = query(
                        collection(db, "orders"),
                        where("channel", "in", ["Online", "ONLINE", "online"]),
                        limit(1)
                    );
                } else {
                    q3 = query(
                        collection(db, "orders"),
                        where("orderChannel", "in", ["Delivery", "DELIVERY", "delivery"]),
                        where("deliveryType", "in", ["Local", "LOCAL", "local"]),
                        limit(1)
                    );
                }
                const snap3 = await getDocs(q3);
                if (!snap3.empty) fetchedDoc = snap3.docs[0].data();
            }

            if (fetchedDoc) {
                setSampleData(fetchedDoc);
                setSampleSource(type === "online" ? "online_live" : "local_live");
                toast({ title: `Loaded latest ${type} order sample`, status: "success" });
            } else {
                toast({ title: `No ${type} orders found in database`, status: "warning" });
            }
        } catch (error) {
            console.error("Error fetching sample:", error);
            toast({ title: "Error fetching sample data", status: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const toggleActive = async (mappingId: string) => {
        try {
            // Deactivate others for same API
            const others = mappings.filter(m => m.apiName === selectedApi && m.id !== mappingId && m.active);
            for (const other of others) {
                await updateDoc(doc(db, "apiMappings", other.id), { active: false });
            }

            // Activate this one
            await updateDoc(doc(db, "apiMappings", mappingId), { active: true });
            loadMappings();
            toast({ title: "Mapping activated", status: "success" });
        } catch (error) {
            toast({ title: "Error activating", status: "error" });
        }
    };

    return (
        <PageShell
            title="API Mapping Tool"
            description="Configure global data transformations."
            wide
            actions={
                <HStack spacing={3}>
                    <Select
                        maxW="180px"
                        value={selectedApi}
                        onChange={(e) => setSelectedApi(e.target.value)}
                        bg="white"
                    >
                        {Object.keys(API_SCHEMAS).map(api => <option key={api} value={api}>{api}</option>)}
                    </Select>
                    <Button leftIcon={<Icon as={FiPlus as any} />} onClick={onOpen}>
                        New Mapping
                    </Button>
                </HStack>
            }
        >
            <VStack spacing={4} align="stretch">

                {/* Compact Table Section */}
                <Box bg={bgCard} border="1px" borderColor={borderColor} borderRadius="xl" overflow="hidden" boxShadow="sm">
                    <Table variant="simple" size="sm">
                        <Thead bg={useColorModeValue("gray.50", "gray.800")}>
                            <Tr>
                                <Th py={3} fontSize="10px">Name</Th>
                                <Th py={3} fontSize="10px">Version</Th>
                                <Th py={3} fontSize="10px">API Name</Th>
                                <Th py={3} fontSize="10px">Status</Th>
                                <Th py={3} fontSize="10px">Created At</Th>
                                <Th py={3} fontSize="10px" textAlign="right">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {mappings.filter(m => m.apiName === selectedApi).map((m) => (
                                <Tr
                                    key={m.id}
                                    bg={selectedMapping?.id === m.id ? "blue.50" : "transparent"}
                                    _dark={{ bg: selectedMapping?.id === m.id ? "blue.900" : "transparent" }}
                                    onClick={() => setSelectedMapping(m)}
                                    cursor="pointer"
                                    transition="background 0.2s"
                                >
                                    <Td py={2}>
                                        <HStack spacing={2} onClick={(e) => {
                                            e.stopPropagation();
                                            setMappingToRename(m);
                                            setRenameValue(m.name || "");
                                            onRenameOpen();
                                        }}>
                                            <Icon as={FiEdit3 as any} color="blue.500" boxSize={3.5} />
                                            <Text fontWeight="bold" fontSize="xs" isTruncated maxW="250px">
                                                {m.name || `Mapping v${m.version}`}
                                            </Text>
                                        </HStack>
                                    </Td>
                                    <Td py={2} fontSize="xs">v{m.version}</Td>
                                    <Td py={2} fontSize="xs">{m.apiName}</Td>
                                    <Td py={2}>
                                        {m.active ? (
                                            <Badge colorScheme="green" variant="subtle" px={2} borderRadius="full" fontSize="9px">
                                                <HStack spacing={1}><Icon as={FiCheckCircle as any} boxSize={2} /> <Text>Active</Text></HStack>
                                            </Badge>
                                        ) : (
                                            <Badge colorScheme="gray" variant="subtle" px={2} borderRadius="full" fontSize="9px">
                                                <HStack spacing={1}><Icon as={FiClock as any} boxSize={2} /> <Text>Inactive</Text></HStack>
                                            </Badge>
                                        )}
                                    </Td>
                                    <Td py={2} fontSize="xs" color="gray.500">{m.createdAt?.toDate?.()?.toLocaleDateString() || "Just now"}</Td>
                                    <Td py={2} textAlign="right">
                                        <Menu isLazy>
                                            <MenuButton as={IconButton} icon={<Icon as={FiMoreVertical as any} />} variant="ghost" size="xs" />
                                            <MenuList fontSize="xs" minW="140px">
                                                <MenuItem icon={<Icon as={FiCheckCircle as any} />} onClick={() => toggleActive(m.id)}>Set Active</MenuItem>
                                                <MenuItem icon={<Icon as={FiLayers as any} />} onClick={() => { setSelectedMapping(m); setIsEditing(true); }}>Edit Map</MenuItem>
                                                <MenuItem icon={<Icon as={FiEdit3 as any} />} onClick={(e) => { e.stopPropagation(); setMappingToRename(m); setRenameValue(m.name || ""); onRenameOpen(); }}>Rename</MenuItem>
                                                <MenuItem icon={<Icon as={FiTrash2 as any} />} color="red.500">Delete</MenuItem>
                                            </MenuList>
                                        </Menu>
                                    </Td>
                                </Tr>
                            ))}
                            {mappings.filter(m => m.apiName === selectedApi).length === 0 && (
                                <Tr><Td colSpan={6} textAlign="center" py={4} color="gray.500" fontSize="xs">No mappings found for {selectedApi}.</Td></Tr>
                            )}
                        </Tbody>
                    </Table>
                </Box>

                {/* Mapping Tool Section */}
                {selectedMapping && (
                    <Box p={5} bg={bgCard} border="1px" borderColor={borderColor} borderRadius="2xl" boxShadow="lg" w="full">
                        <VStack align="stretch" spacing={4} w="full">
                            <HStack justify="space-between">
                                <HStack spacing={3}>
                                    <Icon as={FiLayers as any} color="blue.500" boxSize={5} />
                                    <VStack align="start" spacing={0}>
                                        <Heading size="xs" textTransform="uppercase" letterSpacing="wider" color="gray.500">
                                            Current Workspace
                                        </Heading>
                                        <Text fontWeight="bold" fontSize="lg">{selectedMapping.name || `Mapping v${selectedMapping.version}`} (v{selectedMapping.version})</Text>
                                    </VStack>
                                </HStack>

                                <HStack spacing={4}>
                                    {isEditing ? (
                                        <HStack spacing={2}>
                                            <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>Cancel</Button>
                                            <Button size="sm" colorScheme="green" leftIcon={<Icon as={FiSave as any} />} onClick={handleSave}>Save Changes</Button>
                                        </HStack>
                                    ) : (
                                        <Button size="sm" variant="outline" colorScheme="blue" onClick={() => setIsEditing(true)}>Enter Editor</Button>
                                    )}
                                </HStack>
                            </HStack>

                            <Divider />

                            <Box pointerEvents={isEditing ? "auto" : "none"} opacity={isEditing ? 1 : 0.6} transition="all 0.3s">
                                <FieldMappingTool
                                    targetFields={API_SCHEMAS[selectedMapping.apiName].targets}
                                    sampleData={sampleData}
                                    mapping={selectedMapping.mapping}
                                    apiName={selectedMapping.apiName}
                                    sampleSource={sampleSource}
                                    availableApis={Object.keys(API_SCHEMAS)}
                                    onChange={(newMap) => setSelectedMapping((prev: any) => prev ? { ...prev, mapping: newMap } : null)}
                                    onSampleSourceChange={(source) => {
                                        setSampleSource(source);
                                        if (source === 'mock') {
                                            setSampleData(SAMPLE_ORDER_DEFAULT);
                                        } else if (source === 'online_static') {
                                            setSampleData(ONLINE_STATIC_ORDER);
                                        } else if (source === 'local_static') {
                                            setSampleData(LOCAL_STATIC_ORDER);
                                        } else if (source === 'online_live') {
                                            fetchSampleData("online");
                                        } else if (source === 'local_live') {
                                            fetchSampleData("local");
                                        }
                                    }}
                                    onApiChange={(newApi) => {
                                        setSelectedApi(newApi);
                                        const firstMap = mappings.find(m => m.apiName === newApi);
                                        if (firstMap) setSelectedMapping(firstMap);
                                    }}
                                />
                            </Box>
                        </VStack>
                    </Box>
                )}
            </VStack>

            {/* Modal for Setting New Mapping Name */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="2xl">
                    <ModalHeader fontSize="md">New Mapping Configuration</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel fontSize="sm">Mapping Name</FormLabel>
                            <Input
                                placeholder="e.g. Standard SuperRoute v1"
                                value={newMappingName}
                                onChange={(e) => setNewMappingName(e.target.value)}
                                autoFocus
                                borderRadius="lg"
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose} size="sm">Cancel</Button>
                        <Button colorScheme="blue" onClick={createNewVersion} size="sm" borderRadius="lg">Create</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal for Renaming Mapping */}
            <Modal isOpen={isRenameOpen} onClose={onRenameClose} isCentered size="sm">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent borderRadius="2xl">
                    <ModalHeader fontSize="md">Rename Mapping</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <FormControl>
                            <FormLabel fontSize="sm">New Name</FormLabel>
                            <Input
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                autoFocus
                                borderRadius="lg"
                            />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onRenameClose} size="sm">Cancel</Button>
                        <Button colorScheme="blue" onClick={handleRename} size="sm" borderRadius="lg">Update Name</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </PageShell>
    );
};

export default APIMappingTool;
